import { type GuardStatus, UserCredentials, type UserRequirement, useSettings } from '@capsicum/common';
import { ModalService } from '@capsicum/common-web';
import { useRequirement } from '@capsicum/features/regulations/shared';
import { RequirementsPageCredentialsNeeded, useNCWBalance } from '@capsicum/socios-core';
import { useCredentialsHandler, useDeniedRequirementsModal } from '@capsicum/socios-core-web';
import { atom, useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import { WCPledgeRequirementModal } from '../ui/WCPledgeRequirementModal';

import { useBaseWCPledgeGuard } from './useBaseWCPledgeGuard';

export const wcPledgeAcceptedRequirementAtom = atom<boolean>(false);

export const useWCPledgeRequirementsGuard = () => {
	const baseGuard = useBaseWCPledgeGuard();
	const [wcPledgeRequirementHandled, setWCPledgeRequirementHandled] = useAtom(wcPledgeAcceptedRequirementAtom);

	const [status] = useState<GuardStatus>(baseGuard.status === 'denied' ? 'denied' : 'allowed');
	const {
		settings: { repeatFtoDisclaimer }
	} = useSettings();

	const { isPending: isPendingNCWBalance } = useNCWBalance();

	const { showDeniedRequirementModal } = useDeniedRequirementsModal({
		isLogoutModal: true,
		onDismiss: baseGuard.onDenied
	});

	const { requirement } = useRequirement({
		requirementIdentifier: 'fto_crypto_disclaimer',
		showDeniedRequirementModal
	});

	const checkCryptoDisclaimer = useCallback(() => {
		if (wcPledgeRequirementHandled) return;

		if (repeatFtoDisclaimer) {
			setWCPledgeRequirementHandled(true);

			ModalService.addModal({
				detached: true,
				renderComponent: ({ close }) => (
					<WCPledgeRequirementModal
						closeModal={close}
						requirement={requirement as UserRequirement}
						testID="wc-pledge-disclaimer"
						onAgree={close}
						onDisagree={baseGuard.onDenied}
					/>
				),
				size: 'large'
			});

			return;
		}

		setWCPledgeRequirementHandled(true);
	}, [
		wcPledgeRequirementHandled,
		repeatFtoDisclaimer,
		setWCPledgeRequirementHandled,
		requirement,
		baseGuard.onDenied
	]);

	const { handleCredential: handleWCPledgeCredentials } = useCredentialsHandler({
		credentialCodes: [UserCredentials.ACCESS_LOCKER_ROOM],
		onCredentialGranted: checkCryptoDisclaimer,
		page: RequirementsPageCredentialsNeeded.worldChampionshipPledge
	});

	useEffect(() => {
		if (isPendingNCWBalance || wcPledgeRequirementHandled) return;

		handleWCPledgeCredentials();
	}, [handleWCPledgeCredentials, isPendingNCWBalance, wcPledgeRequirementHandled]);

	return {
		onDenied: baseGuard.onDenied,
		status
	};
};
